import { getUrlWithQueryString } from '@common/utils'
import { FastlyImage } from '@components/common/image'
import Modal from '@components/common/modal'
import { COURSES, CourseType } from '@lib/data/summer-coding-camp'
import { useRouter } from 'next/router'
import { useEffect, useState } from 'react'
import { BookOpen, Check, CheckCircle, FileText, User } from 'react-feather'

const COMMON_IMAGE_PATH = '/images/coding-summer-camp/'

type CoursesProps = {
  couponCode: string
  title: JSX.Element
}

const Courses = ({ couponCode, title }: CoursesProps) => {
  const [isOpen, setIsOpen] = useState(false)
  const [selectedCourse, setSelectedCourse] = useState<CourseType | null>(null)
  const router = useRouter()
  const queryString = router.asPath.split('?')[1]
  const handleOpenModal = (course: CourseType) => {
    setSelectedCourse(course)
    setIsOpen(true)
  }
  const [destinationURL, setDestinationURL] = useState<string>('')

  useEffect(() => {
    if (selectedCourse) {
      setDestinationURL(
        getUrlWithQueryString(
          `${selectedCourse.paymentLinkURL}?prefilled_promo_code=${couponCode}`,
          queryString
        )
      )
    }
  }, [selectedCourse])

  return (
    <div
      className="container mx-auto flex flex-col space-y-10 leading-tight py-16 text-grey px-5"
      id="courses"
    >
      <div className="text-center">
        <h2 className="text-2xl lg:text-4xl font-600">{title}</h2>
      </div>

      <Modal
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        isCrossIconShown
        width="w-full h-full max-w-5xl mt-12 sm:mt-5"
        shouldCloseOnEsc
      >
        {selectedCourse && (
          <>
            <h3 className="text-lg lg:text-2xl font-700 pb-1">
              {selectedCourse.curriculumCard.title}
            </h3>
            <p>{selectedCourse.curriculumCard.description}</p>
            <div className="flex gap-2 lg:gap-6 pt-3 flex-wrap">
              <div className="flex items-center">
                <BookOpen className="text-orange w-4 mr-1" />
                <span>{selectedCourse.sessions} Sessions</span>
              </div>
              <div className="flex items-center">
                <FileText className="text-orange w-4 mr-1" />

                <span>{selectedCourse.curriculumCard.projects}+ Projects</span>
              </div>
              <div className="flex items-center">
                <CheckCircle className="text-orange w-4 mr-1" />
                <span>{selectedCourse.curriculumCard.quizzes} Quizzes</span>
              </div>
            </div>

            <div className="w-full border border-grey-300 my-4" />

            {/* Stripe payment link */}
            <div className="flex space-x-2 items-center justify-center my-6">
              <a
                className="text-center btn btn-orange w-full md:w-auto py-3 text-lg"
                href={destinationURL}
              >
                Claim your spot
              </a>
            </div>

            <h4 className="text-lg lg:text-xl font-600 pb-2">
              What will you learn
            </h4>

            <div className="border border-orange-300 rounded-md p-3 bg-orange-100 bg-opacity-50">
              {selectedCourse.curriculumCard.lessonPlan.map(
                (lessonPlan, index) => (
                  <>
                    <div key={index} className="flex gap-2">
                      {/* <FastlyImage src={`#`} alt={'#'} width={50} height={50} />  */}
                      <div>
                        <h4 className="font-500">
                          Lesson {index + 1}: {lessonPlan.title}
                        </h4>
                        <p>{lessonPlan.description}</p>
                      </div>
                    </div>
                    {index <
                      selectedCourse.curriculumCard.lessonPlan.length - 1 && (
                      <div className="w-full border border-orange-200 my-4" />
                    )}
                  </>
                )
              )}
            </div>
            {/* Stripe payment link */}
            <div className="flex space-x-2 items-center justify-center mt-12">
              <a
                className="text-center btn btn-orange w-full md:w-auto py-3 text-lg"
                href={destinationURL}
              >
                Claim your spot
              </a>
            </div>

            <div className="flex flex-wrap justify-center gap-6 text-grey-900 px-4 pt-12">
              <div className="pb-8 rounded-lg bg-blue-100 bg-opacity-25 relative">
                <span className="absolute text-3xl">🚀</span>
                <div className="flex items-center pt-8">
                  <div
                    className="w-2 h-4"
                    style={{
                      backgroundColor: '#AD4D9E',
                    }}
                  />
                  <div className="font-600 text-xl pl-6">
                    Key Learning Outcomes
                  </div>
                </div>
                {selectedCourse.curriculumCard.keyLearningOutcomes.map(
                  (outcome, index) => (
                    <div key={index} className="flex items-center text-sm px-4">
                      <span
                        className="rounded-full bg-green-50 bg-opacity-50"
                        style={{ padding: '2px' }}
                      >
                        <Check
                          height={10}
                          width={10}
                          strokeWidth={3}
                          className="text-green-200"
                        />
                      </span>
                      <p className="ml-1 text-left">{outcome}</p>
                    </div>
                  )
                )}
              </div>

              <div className="pb-8 rounded-lg bg-red-100 bg-opacity-25 relative">
                <span className="absolute text-3xl">🌟</span>
                <div className="flex items-center pt-8">
                  <div
                    className="w-2 h-4"
                    style={{
                      backgroundColor: '#AD4D9E',
                    }}
                  />
                  <div className="font-600 text-xl pl-6">
                    Exclusive Benefits
                  </div>
                </div>

                {selectedCourse.curriculumCard.exclusiveBenifits.map(
                  (outcome, index) => (
                    <div key={index} className="flex items-center text-sm px-4">
                      <span
                        className="rounded-full bg-orange-200"
                        style={{ padding: '2px' }}
                      >
                        <Check
                          height={10}
                          width={10}
                          strokeWidth={3}
                          className="text-orange"
                        />
                      </span>
                      <p className="ml-1 text-left">{outcome}</p>
                    </div>
                  )
                )}
              </div>
            </div>
            {/* Stripe payment link */}
            <div className="flex space-x-2 items-center justify-center my-12">
              <a
                className="text-center btn btn-orange w-full md:w-auto py-3 text-lg"
                href={destinationURL}
              >
                Claim your spot
              </a>
            </div>
          </>
        )}
      </Modal>

      <div className="flex flex-wrap justify-center">
        {COURSES.map((course, i) => (
          <div
            key={i}
            className="p-2 w-full md:w-1/2 lg:w-1/3 xl:w-1/4 max-w-md"
          >
            <div className="p-3 shadow hover:shadow-lg transition-all duration-200 rounded-md">
              <div className="relative w-full">
                <FastlyImage
                  onClick={() => handleOpenModal(course)}
                  src={`${COMMON_IMAGE_PATH}/courses/${course.image}`}
                  alt={course.altText}
                  className="cursor-pointer max-w-full"
                  width={530}
                  height={244}
                />
              </div>

              <div className="space-y-3 pt-3 pb-2">
                <h3
                  onClick={() => handleOpenModal(course)}
                  className="text-xl leading-tight font-700 cursor-pointer hover:text-orange duration-300"
                >
                  {course.title}
                </h3>
                <div className="font-500">
                  <div className="flex items-center">
                    <BookOpen className="text-orange w-3 mr-2" />
                    <span>{course.sessions} x 1-hour Sessions</span>
                  </div>
                  <div className="flex items-center">
                    <User className="text-orange w-3 mr-2" />
                    <span>Ages {course.age} years</span>
                  </div>
                </div>

                <p className="h-13">{course.description}</p>

                <h4 className="font-600">Learning outcomes</h4>

                <div className="space-y-1" style={{ height: '88px' }}>
                  {course.outcomes.map((outcome, index) => (
                    <div key={index} className="flex items-start">
                      <span
                        className="rounded-full bg-orange-200"
                        style={{ padding: '2px', marginTop: '2px' }}
                      >
                        <Check
                          height={10}
                          width={10}
                          strokeWidth={3}
                          className="text-orange"
                        />
                      </span>
                      <p className="ml-1 text-left">{outcome}</p>
                    </div>
                  ))}
                </div>
              </div>
              <button
                className="text-orange underline text-lg w-full font-700 mb-3 focus:outline-none hover:text-orange-500"
                onClick={() => handleOpenModal(course)}
              >
                Know more
              </button>
              <div className="flex space-x-2 items-center justify-center">
                <a
                  className="btn text-white bg-orange hover:bg-orange-500 hover:shadow hover:text-white duration-300 w-full text-center text-lg"
                  href={`${course.paymentLinkURL}?prefilled_promo_code=${couponCode}`}
                >
                  Claim your spot
                </a>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

export default Courses
